import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import ScoreBadge from "../components/ScoreBadge";
import { Sprout, Newspaper, Send, Trash2, RefreshCw, Plus, Link as LinkIcon, FileText } from "lucide-react";
import LeadDrawer from "../components/LeadDrawer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

export default function Nurture() {
  const [leads, setLeads] = useState([]);
  const [focus, setFocus] = useState(null);
  const [newsletters, setNewsletters] = useState([]);
  const [settings, setSettings] = useState({ newsletter_monthly_enabled: false, linkedin_newsletter_url: "" });
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");
  const [syncing, setSyncing] = useState(false);
  // Plan B — fetch from a single LinkedIn article URL
  const [articleUrl, setArticleUrl] = useState("");
  const [fetchingArticle, setFetchingArticle] = useState(false);
  // Plan C — manual paste form
  const [showManual, setShowManual] = useState(false);
  const [manualDraft, setManualDraft] = useState({ title: "", url: "", summary: "" });
  const [savingManual, setSavingManual] = useState(false);

  const loadNewsletters = () => api.get("/newsletters").then((r) => setNewsletters(r.data.items || [])).catch(() => {});

  useEffect(() => {
    api.get("/leads", { params: { status: "nurture" } }).then((r) => setLeads(r.data)).catch(() => {});
    loadNewsletters();
    api.get("/settings").then((r) => {
      setSettings(r.data || {});
      setUrlDraft(r.data?.linkedin_newsletter_url || "");
    }).catch(() => {});
  }, []);

  const saveSetting = async (next) => {
    setSettings(next);
    try {
      await api.patch("/settings", {
        newsletter_monthly_enabled: !!next.newsletter_monthly_enabled,
        nurture_engagement_enabled: next.nurture_engagement_enabled !== false,
        nurture_trigger_emails_enabled: next.nurture_trigger_emails_enabled !== false,
        nurture_anniversary_enabled: next.nurture_anniversary_enabled !== false,
        linkedin_newsletter_url: next.linkedin_newsletter_url ?? null,
      });
      toast.success("Saved");
    } catch { toast.error("Could not save"); }
  };

  const saveNewsletterUrl = async () => {
    const u = (urlDraft || "").trim();
    if (u && !u.toLowerCase().includes("linkedin.com/in/")) {
      toast.error("URL must be a LinkedIn profile URL (e.g. https://www.linkedin.com/in/yourname/recent-activity/newsletter/)");
      return;
    }
    await saveSetting({ ...settings, linkedin_newsletter_url: u });
    setEditingUrl(false);
    if (u) {
      // Kick off an immediate sync so the user sees newsletters appear right away
      await syncFromLinkedIn();
    }
  };

  const syncFromLinkedIn = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/newsletters/sync-from-linkedin");
      if (data?.ok) {
        toast.success(`Sync done · ${data.inserted} new, ${data.skipped_duplicates} already in library`);
        await loadNewsletters();
      } else {
        const status = data?.status ? ` [HTTP ${data.status}]` : "";
        const msg = data?.hint
          ? `${data.reason}${status}: ${data.hint}`
          : (data?.detail ? `${data.reason}${status} — ${data.detail}` : (data?.reason || "Sync failed"));
        toast.error(msg, { duration: 12000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const addFromUrl = async () => {
    const u = (articleUrl || "").trim();
    if (!u || !u.toLowerCase().includes("linkedin.com")) {
      toast.error("Paste a LinkedIn article URL (linkedin.com/pulse/... or linkedin.com/newsletters/...)");
      return;
    }
    setFetchingArticle(true);
    try {
      const { data } = await api.post("/newsletters/from-url", { url: u });
      if (data?.duplicate) toast.info(`Already in library: ${data.title}`);
      else toast.success(`Added: ${data.title}`);
      setArticleUrl("");
      await loadNewsletters();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch article");
    } finally { setFetchingArticle(false); }
  };

  const addManual = async () => {
    if (!manualDraft.title.trim()) { toast.error("Title is required"); return; }
    if (!manualDraft.summary.trim() && !manualDraft.url.trim()) {
      toast.error("Add either a summary or a URL so leads have something to read");
      return;
    }
    setSavingManual(true);
    try {
      await api.post("/newsletters", manualDraft);
      setManualDraft({ title: "", url: "", summary: "" });
      setShowManual(false);
      await loadNewsletters();
      toast.success("Newsletter added to library");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSavingManual(false); }
  };

  const dispatchNow = async (newsletter_id) => {
    if (!window.confirm("Send this newsletter NOW to all nurture leads who haven't received it yet?")) return;
    try {
      const { data } = await api.post(`/newsletters/${newsletter_id}/dispatch-now`);
      if (data.errors > 0 && data.sent === 0) {
        // All failed — surface the error sample + hint
        const sample = (data.error_samples && data.error_samples[0]) || "Unknown error";
        const hint = data.hint ? `\n\nFix: ${data.hint}` : "";
        toast.error(`All ${data.errors} sends failed. Sample error: ${sample}${hint}`, { duration: 18000 });
      } else if (data.errors > 0) {
        const hint = data.hint ? ` · ${data.hint}` : "";
        toast.warning(`Sent: ${data.sent} · errors: ${data.errors}${hint}`, { duration: 12000 });
      } else {
        toast.success(`Sent: ${data.sent} · errors: 0`);
      }
      await loadNewsletters();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dispatch failed"); }
  };

  const sendTest = async (newsletter_id) => {
    const to = window.prompt("Send test newsletter to which email?", "gopani@gmail.com");
    if (!to) return;
    try {
      const { data } = await api.post("/newsletters/send-test", { to_email: to, newsletter_id });
      if (data.ok) toast.success(`Test sent to ${to} via ${data.via}`);
      else toast.error(data.error || "Send failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const deleteNewsletter = async (newsletter_id) => {
    if (!window.confirm("Delete this newsletter from the library?")) return;
    try {
      await api.delete(`/newsletters/${newsletter_id}`);
      await loadNewsletters();
    } catch { toast.error("Delete failed"); }
  };

  const allNurture = leads;

  return (
    <Layout title="Nurture" subtitle="Long-term, 3-12 months">
      <div className="bg-card border border-border p-6 mb-6" style={{ borderRadius: 2 }}>
        <div className="flex items-center gap-3 mb-3">
          <Sprout className="h-5 w-5 text-primary" strokeWidth={1.5} />
          <div className="font-serif text-xl tracking-tight" style={{ fontWeight: 450 }}>Stay in front — without being a pest.</div>
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Nurture leads receive monthly value (industry benchmarks, regulation changes, procurement trends) over 3-12 months. The goal is familiarity, authority, and timing — never pressure. Leads land here automatically when they reply &ldquo;not now / check back in a few months&rdquo;.
        </p>
      </div>

      {/* ── Background nurture levers (auto-running loops) ─────────────────── */}
      <div className="bg-card border border-border mb-6" style={{ borderRadius: 2 }} data-testid="nurture-levers">
        <div className="px-6 py-4 border-b border-border">
          <div className="font-medium">Background nurture loops</div>
          <div className="text-xs text-muted-foreground mt-0.5">Three quiet, low-touch levers running automatically per nurture lead.</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
          <div className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-sm">LinkedIn engagement</div>
              <Switch
                data-testid="nurture-engagement-toggle"
                checked={settings.nurture_engagement_enabled !== false}
                onCheckedChange={(v) => saveSetting({ ...settings, nurture_engagement_enabled: !!v })}
              />
            </div>
            <p className="text-xs text-muted-foreground">Auto-likes one recent post per nurture lead, once every 28 days. Pure familiarity, zero noise.</p>
          </div>
          <div className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-sm">Trigger-event emails</div>
              <Switch
                data-testid="nurture-trigger-toggle"
                checked={settings.nurture_trigger_emails_enabled !== false}
                onCheckedChange={(v) => saveSetting({ ...settings, nurture_trigger_emails_enabled: !!v })}
              />
            </div>
            <p className="text-xs text-muted-foreground">When their company has news (funding, exec change, expansion), AI drafts a 1:1 email. Max one per 90 days per lead.</p>
          </div>
          <div className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-sm">Anniversary voice note</div>
              <Switch
                data-testid="nurture-anniversary-toggle"
                checked={settings.nurture_anniversary_enabled !== false}
                onCheckedChange={(v) => saveSetting({ ...settings, nurture_anniversary_enabled: !!v })}
              />
            </div>
            <p className="text-xs text-muted-foreground">Once a year on their LinkedIn role anniversary, send a 30-sec voice note via DM. Falls back to text if ElevenLabs offline.</p>
          </div>
        </div>
      </div>

      {/* ── Newsletter library ───────────────────────────────────────────── */}
      <div className="bg-card border border-border mb-6" style={{ borderRadius: 2 }} data-testid="newsletter-library">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Newspaper className="h-5 w-5 text-primary" strokeWidth={1.5} />
            <div>
              <div className="font-medium">Monthly newsletter share</div>
              <div className="text-xs text-muted-foreground">Auto-pulls your LinkedIn newsletter. We send the latest issue to every nurture lead once a month.</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                id="nl-monthly"
                data-testid="newsletter-monthly-toggle"
                checked={!!settings.newsletter_monthly_enabled}
                onCheckedChange={(v) => saveSetting({ ...settings, newsletter_monthly_enabled: !!v })}
              />
              <Label htmlFor="nl-monthly" className="cursor-pointer text-sm">
                {settings.newsletter_monthly_enabled ? "ON — auto-send 1st of month" : "OFF"}
              </Label>
            </div>
            <Button size="sm" variant="outline" onClick={syncFromLinkedIn} disabled={syncing || !settings.linkedin_newsletter_url} data-testid="newsletter-sync-btn">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} strokeWidth={1.5} /> {syncing ? "Syncing…" : "Sync from LinkedIn"}
            </Button>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-border bg-secondary/30">
          <Label className="overline">Your LinkedIn newsletter URL</Label>
          {editingUrl ? (
            <div className="flex items-center gap-2 mt-1">
              <Input
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                placeholder="https://www.linkedin.com/in/yourname/recent-activity/newsletter/"
                className="font-mono text-xs"
                data-testid="newsletter-url-input"
              />
              <Button size="sm" onClick={saveNewsletterUrl} data-testid="newsletter-url-save">Save & sync</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditingUrl(false); setUrlDraft(settings.linkedin_newsletter_url || ""); }}>Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 text-sm font-mono text-muted-foreground truncate">
                {settings.linkedin_newsletter_url || <span className="italic">No URL set — paste your LinkedIn newsletter page URL to start auto-sync</span>}
              </div>
              <Button size="sm" variant="outline" onClick={() => setEditingUrl(true)} data-testid="newsletter-url-edit">
                {settings.linkedin_newsletter_url ? "Edit URL" : "Set URL"}
              </Button>
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-2">
            Open your LinkedIn newsletter homepage and copy the URL (e.g. <span className="font-mono">linkedin.com/in/taranggopani/recent-activity/newsletter/</span>). We&apos;ll auto-sync new issues daily via Unipile.
          </div>
        </div>

        {/* ── Plan B + C: per-article URL fetch, or manual paste ─────────────── */}
        <div className="px-6 py-4 border-b border-border bg-card">
          <div className="overline mb-2">Add a single newsletter</div>

          {/* Plan B — paste article URL → server-side scrape OG meta */}
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <Input
              value={articleUrl}
              onChange={(e) => setArticleUrl(e.target.value)}
              placeholder="Paste any LinkedIn article URL (linkedin.com/pulse/...)"
              className="font-mono text-xs flex-1"
              data-testid="newsletter-article-url-input"
            />
            <Button size="sm" onClick={addFromUrl} disabled={fetchingArticle || !articleUrl.trim()} data-testid="newsletter-from-url-btn">
              {fetchingArticle ? "Fetching…" : "Add from URL"}
            </Button>
          </div>

          {/* Plan C — manual paste */}
          {!showManual ? (
            <button
              type="button"
              onClick={() => setShowManual(true)}
              className="text-xs text-primary underline-offset-2 hover:underline flex items-center gap-1"
              data-testid="newsletter-manual-toggle"
            >
              <FileText className="h-3.5 w-3.5" strokeWidth={1.5} /> Or paste title + summary manually
            </button>
          ) : (
            <div className="space-y-3 mt-3 p-4 bg-secondary/30 border border-border" style={{ borderRadius: 2 }} data-testid="newsletter-manual-form">
              <div>
                <Label className="overline">Title</Label>
                <Input
                  value={manualDraft.title}
                  onChange={(e) => setManualDraft({ ...manualDraft, title: e.target.value })}
                  placeholder="e.g. The CFO playbook for Q3 cost reviews"
                  data-testid="newsletter-manual-title"
                />
              </div>
              <div>
                <Label className="overline">LinkedIn URL (optional)</Label>
                <Input
                  value={manualDraft.url}
                  onChange={(e) => setManualDraft({ ...manualDraft, url: e.target.value })}
                  placeholder="https://www.linkedin.com/pulse/..."
                  data-testid="newsletter-manual-url"
                />
              </div>
              <div>
                <Label className="overline">Summary / preview text (use {"{first_name}"} to personalise)</Label>
                <Textarea
                  rows={5}
                  value={manualDraft.summary}
                  onChange={(e) => setManualDraft({ ...manualDraft, summary: e.target.value })}
                  placeholder="2-4 paragraphs the recipient can read inline."
                  data-testid="newsletter-manual-summary"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setShowManual(false); setManualDraft({ title: "", url: "", summary: "" }); }}>Cancel</Button>
                <Button size="sm" onClick={addManual} disabled={savingManual} data-testid="newsletter-manual-save">
                  <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> {savingManual ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {newsletters.length === 0 ? (
          <div className="px-6 py-10 text-sm text-muted-foreground">
            No newsletters in library yet. {settings.linkedin_newsletter_url ? "Click Sync from LinkedIn to pull your latest issues." : "Paste your LinkedIn newsletter URL above to start."}
          </div>
        ) : (
          newsletters.map((nl) => (
            <div key={nl.newsletter_id} className="tbl-row px-6 py-4 grid grid-cols-12 gap-4 items-center" data-testid={`newsletter-${nl.newsletter_id}`}>
              <div className="col-span-6">
                <div className="font-medium">{nl.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {nl.published_at ? new Date(nl.published_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  {nl.url && <> · <a href={nl.url} target="_blank" rel="noreferrer" className="underline">LinkedIn</a></>}
                </div>
              </div>
              <div className="col-span-3 text-xs text-muted-foreground">
                Sent to {nl.sent_to_nurture_count || 0} leads
                {nl.last_dispatch_at && <div>Last: {new Date(nl.last_dispatch_at).toLocaleDateString("en-AU")}</div>}
              </div>
              <div className="col-span-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => sendTest(nl.newsletter_id)} data-testid={`newsletter-test-${nl.newsletter_id}`} title="Send a test copy to one email">
                  Test
                </Button>
                <Button size="sm" variant="outline" onClick={() => dispatchNow(nl.newsletter_id)} data-testid={`newsletter-send-${nl.newsletter_id}`}>
                  <Send className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Send now
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteNewsletter(nl.newsletter_id)} data-testid={`newsletter-delete-${nl.newsletter_id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="bg-card border border-border" style={{ borderRadius: 2 }} data-testid="nurture-list">
        <div className="px-6 py-3 border-b border-border flex items-center justify-between">
          <div className="overline">Active nurture leads · {allNurture.length}</div>
          <label className="text-xs text-primary cursor-pointer underline hover:no-underline" data-testid="nurture-csv-upload-label">
            <input
              type="file" accept=".csv" className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                const fd = new FormData();
                fd.append("file", f);
                fd.append("mode", "nurture");
                fd.append("tag", "manual_upload");
                try {
                  const { data } = await api.post("/leads-import/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  toast.success(`Imported ${data.created} new · ${data.duplicates} duplicates · ${data.enrolled} enrolled`);
                  api.get("/leads", { params: { status: "nurture" } }).then((r) => setLeads(r.data));
                } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
                e.target.value = "";
              }}
            />
            Upload nurture CSV
          </label>
        </div>
        {allNurture.length === 0 && <div className="px-6 py-10 text-sm text-muted-foreground">No nurture leads yet. Move a lead to the &ldquo;Nurture&rdquo; stage from the lead drawer.</div>}
        {allNurture.map((l) => (
          <div key={l.lead_id} onClick={() => setFocus(l)} className="tbl-row px-6 py-4 grid grid-cols-12 gap-4 items-center cursor-pointer" data-testid={`nurture-${l.lead_id}`}>
            <div className="col-span-5">
              <div className="font-medium">{l.full_name}</div>
              <div className="text-xs text-muted-foreground">{l.title} · {l.company}</div>
            </div>
            <div className="col-span-3 text-sm text-muted-foreground">{l.industry}</div>
            <div className="col-span-2 text-xs text-muted-foreground">{l.last_contacted_at ? `Last touched ${new Date(l.last_contacted_at).toLocaleDateString("en-AU")}` : "Never contacted"}</div>
            <div className="col-span-2 flex justify-end"><ScoreBadge score={l.score} band={l.score_band} /></div>
          </div>
        ))}
      </div>

      <LeadDrawer
        lead={focus}
        onClose={() => setFocus(null)}
        onUpdated={(updated) => {
          setFocus(updated);
          setLeads((prev) => prev.map((x) => (x.lead_id === updated.lead_id ? updated : x)));
        }}
      />
    </Layout>
  );
}
